import { type Component, createSignal, Show, For, createMemo } from 'solid-js'
import { DatePicker, parseDate } from '@ark-ui/solid/date-picker'
import { Portal } from 'solid-js/web'
import { Icon } from '@iconify-icon/solid'
import { cn } from '../../../shared/utils/cn'
import { parseLocalValue } from '../../../shared/utils/local-date'

type Mode = 'date' | 'datetime'

interface DateTimeInputProps {
  readonly value?: string
  readonly onValueChange: (value: string) => void
  readonly placeholder?: string
  readonly mode?: Mode
  readonly class?: string
  readonly hasError?: boolean
  readonly disabled?: boolean
}

const inputBase =
  'w-full h-9 px-3 py-2 bg-white rounded-md border text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 cursor-pointer'
const inputNormal = cn(inputBase, 'border-gray-200 focus:ring-primary-500 focus:border-primary-500')
const inputError = cn(inputBase, 'border-red-300 focus:ring-red-500 focus:border-red-500')
const inputDisabled = cn(inputBase, 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed')

const pad = (n: number): string => String(n).padStart(2, '0')

const DateTimeInput: Component<DateTimeInputProps> = (props) => {
  const mode = () => props.mode ?? 'date'
  const [hour, setHour] = createSignal(0)
  const [minute, setMinute] = createSignal(0)

  // Parse initial value if provided
  const initialDate = createMemo(() => {
    if (!props.value) return undefined
    try {
      const d = parseLocalValue(props.value)
      if (!d) return undefined
      setHour(d.getHours())
      setMinute(d.getMinutes())
      return parseDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)
    } catch {
      return undefined
    }
  })

  const emitValue = (dateStr: string, h?: number, m?: number): void => {
    if (mode() === 'datetime') {
      props.onValueChange(`${dateStr}T${pad(h ?? hour())}:${pad(m ?? minute())}`)
    } else {
      props.onValueChange(dateStr)
    }
  }

  const displayValue = createMemo(() => {
    if (!props.value) return ''
    try {
      const d = parseLocalValue(props.value)
      if (!d) return props.value
      const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      if (mode() === 'datetime') {
        const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        return `${datePart} ${timePart}`
      }
      return datePart
    } catch {
      return props.value
    }
  })

  return (
    <DatePicker.Root
      value={initialDate() ? [initialDate()!] : []}
      onValueChange={(details) => {
        if (details.value.length > 0) {
          const v = details.value[0]
          const dateStr = `${v.year}-${pad(v.month)}-${pad(v.day)}`
          emitValue(dateStr)
        }
      }}
      closeOnSelect={mode() === 'date'}
      positioning={{ placement: 'bottom-start' }}
      disabled={props.disabled}
    >
      <DatePicker.Control class="relative">
        <DatePicker.Trigger
          class={props.disabled ? inputDisabled : props.hasError ? inputError : inputNormal}
          style={{ 'text-align': 'left' }}
        >
          <div class="flex items-center gap-2">
            <Icon icon="heroicons:calendar-days" class="text-[1rem] text-gray-400 shrink-0" />
            <span classList={{ 'text-gray-400': !displayValue(), 'text-gray-900': !!displayValue() }}>
              {displayValue() || props.placeholder || (mode() === 'datetime' ? 'Select date & time' : 'Select date')}
            </span>
          </div>
        </DatePicker.Trigger>
      </DatePicker.Control>

      <Portal>
        <DatePicker.Positioner>
          <DatePicker.Content class="bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-50 animate-menu-enter">
            <DatePicker.View view="day">
              <DatePicker.Context>
                {(context) => (
                  <>
                    {/* Header: prev / month-year / next */}
                    <DatePicker.ViewControl class="flex items-center justify-between mb-2">
                      <DatePicker.PrevTrigger class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors duration-150">
                        <Icon icon="heroicons:chevron-left" class="text-[1rem]" />
                      </DatePicker.PrevTrigger>
                      <DatePicker.ViewTrigger class="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors duration-150">
                        <DatePicker.RangeText />
                      </DatePicker.ViewTrigger>
                      <DatePicker.NextTrigger class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors duration-150">
                        <Icon icon="heroicons:chevron-right" class="text-[1rem]" />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>

                    {/* Day grid */}
                    <DatePicker.Table class="w-full">
                      <DatePicker.TableHead>
                        <DatePicker.TableRow>
                          <For each={context().weekDays}>
                            {(weekDay) => (
                              <DatePicker.TableHeader class="text-xs font-medium text-gray-500 pb-1 w-8 text-center">
                                {weekDay.narrow}
                              </DatePicker.TableHeader>
                            )}
                          </For>
                        </DatePicker.TableRow>
                      </DatePicker.TableHead>
                      <DatePicker.TableBody>
                        <For each={context().weeks}>
                          {(week) => (
                            <DatePicker.TableRow>
                              <For each={week}>
                                {(day) => (
                                  <DatePicker.TableCell value={day}>
                                    <DatePicker.TableCellTrigger
                                      class="w-8 h-8 rounded-full text-sm flex items-center justify-center transition-colors duration-100
                                        data-[today]:font-bold data-[today]:text-primary-600
                                        data-[selected]:bg-primary-600 data-[selected]:text-white
                                        data-[outside-range]:text-gray-300
                                        hover:bg-gray-100 data-[selected]:hover:bg-primary-700"
                                    >
                                      {day.day}
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                )}
                              </For>
                            </DatePicker.TableRow>
                          )}
                        </For>
                      </DatePicker.TableBody>
                    </DatePicker.Table>

                    {/* Time selector (datetime mode only) */}
                    <Show when={mode() === 'datetime'}>
                      <div class="border-t border-gray-200 mt-2 pt-2 flex items-center justify-center gap-2">
                        <Icon icon="heroicons:clock" class="text-[1rem] text-gray-400" />
                        {/* Hour */}
                        <div class="flex flex-col items-center">
                          <button
                            type="button"
                            class="w-8 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-100"
                            onClick={() => {
                              const h = (hour() + 1) % 24
                              setHour(h)
                              if (props.value) {
                                const d = props.value.split('T')[0]
                                if (d) emitValue(d, h, minute())
                              }
                            }}
                          >
                            <Icon icon="heroicons:chevron-up" class="text-[0.75rem]" />
                          </button>
                          <span class="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-sm font-medium text-gray-900 tabular-nums">
                            {pad(hour())}
                          </span>
                          <button
                            type="button"
                            class="w-8 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-100"
                            onClick={() => {
                              const h = (hour() - 1 + 24) % 24
                              setHour(h)
                              if (props.value) {
                                const d = props.value.split('T')[0]
                                if (d) emitValue(d, h, minute())
                              }
                            }}
                          >
                            <Icon icon="heroicons:chevron-down" class="text-[0.75rem]" />
                          </button>
                        </div>
                        <span class="text-gray-400 font-medium text-lg">:</span>
                        {/* Minute */}
                        <div class="flex flex-col items-center">
                          <button
                            type="button"
                            class="w-8 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-100"
                            onClick={() => {
                              const m = (minute() + 5) % 60
                              setMinute(m)
                              if (props.value) {
                                const d = props.value.split('T')[0]
                                if (d) emitValue(d, hour(), m)
                              }
                            }}
                          >
                            <Icon icon="heroicons:chevron-up" class="text-[0.75rem]" />
                          </button>
                          <span class="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-sm font-medium text-gray-900 tabular-nums">
                            {pad(minute())}
                          </span>
                          <button
                            type="button"
                            class="w-8 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-100"
                            onClick={() => {
                              const m = (minute() - 5 + 60) % 60
                              setMinute(m)
                              if (props.value) {
                                const d = props.value.split('T')[0]
                                if (d) emitValue(d, hour(), m)
                              }
                            }}
                          >
                            <Icon icon="heroicons:chevron-down" class="text-[0.75rem]" />
                          </button>
                        </div>
                      </div>
                    </Show>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>

            {/* Month view */}
            <DatePicker.View view="month">
              <DatePicker.Context>
                {(context) => (
                  <>
                    <DatePicker.ViewControl class="flex items-center justify-between mb-2">
                      <DatePicker.PrevTrigger class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors duration-150">
                        <Icon icon="heroicons:chevron-left" class="text-[1rem]" />
                      </DatePicker.PrevTrigger>
                      <DatePicker.ViewTrigger class="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors duration-150">
                        <DatePicker.RangeText />
                      </DatePicker.ViewTrigger>
                      <DatePicker.NextTrigger class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors duration-150">
                        <Icon icon="heroicons:chevron-right" class="text-[1rem]" />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>
                    <DatePicker.Table class="w-full">
                      <DatePicker.TableBody>
                        <For each={context().getMonthsGrid({ columns: 4, format: 'short' })}>
                          {(months) => (
                            <DatePicker.TableRow>
                              <For each={months}>
                                {(month) => (
                                  <DatePicker.TableCell value={month.value}>
                                    <DatePicker.TableCellTrigger class="w-full h-8 rounded-md text-sm flex items-center justify-center hover:bg-gray-100 data-[selected]:bg-primary-600 data-[selected]:text-white transition-colors duration-100">
                                      {month.label}
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                )}
                              </For>
                            </DatePicker.TableRow>
                          )}
                        </For>
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>

            {/* Year view */}
            <DatePicker.View view="year">
              <DatePicker.Context>
                {(context) => (
                  <>
                    <DatePicker.ViewControl class="flex items-center justify-between mb-2">
                      <DatePicker.PrevTrigger class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors duration-150">
                        <Icon icon="heroicons:chevron-left" class="text-[1rem]" />
                      </DatePicker.PrevTrigger>
                      <DatePicker.ViewTrigger class="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors duration-150">
                        <DatePicker.RangeText />
                      </DatePicker.ViewTrigger>
                      <DatePicker.NextTrigger class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors duration-150">
                        <Icon icon="heroicons:chevron-right" class="text-[1rem]" />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>
                    <DatePicker.Table class="w-full">
                      <DatePicker.TableBody>
                        <For each={context().getYearsGrid({ columns: 4 })}>
                          {(years) => (
                            <DatePicker.TableRow>
                              <For each={years}>
                                {(year) => (
                                  <DatePicker.TableCell value={year.value}>
                                    <DatePicker.TableCellTrigger class="w-full h-8 rounded-md text-sm flex items-center justify-center hover:bg-gray-100 data-[selected]:bg-primary-600 data-[selected]:text-white transition-colors duration-100">
                                      {year.label}
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                )}
                              </For>
                            </DatePicker.TableRow>
                          )}
                        </For>
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  )
}

export default DateTimeInput
